import React, { useEffect, useState } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import * as ROUTES from "../../constants/routes";
import Label from "./Label";
import "../../scss/home.scss";
import Account from "./Account";
import history from "../../History";
import { connect } from "react-redux";
import CreateLabel from "./Label/CreateLabel";
import Dashboard from "./Dashboard";
import SideBar from "./SideBar";
import Header from "./Header";
import Orders from "./Orders/Orders";
import Manifests from "./Manifests/Manifests";
import CreateManifest from "./Manifests/CreateManifest";
import ManifestDetails from "./Manifests/ManifestDetails";
import AddMoreLabels from "./Manifests/AddLabels";

import { useCookies } from "react-cookie";
import { ReactComponent as TestGear } from "../../assets/test-mode-icon.svg";
import {
  getAccountInfo,
  getOrganizations,
  logoutSession,
  testSessionAuth,
} from "../actions";
import { ReactComponent as BoxLogo } from "../../assets/box-in-logo.svg";
import { getBusinessDetails } from "../actions/BusinessActions";
import {
  getUserCountry,
  setApplicationContext,
  setContextReturnURL,
} from "../actions/ApplicationContextActions";
import HelpCenter from "./HelpCenter";
import { SESSION_AUTH } from "../../constants/types";
import { FiAlertCircle } from "react-icons/fi";
import { Toaster } from "react-hot-toast";
import Stores from "./Checkouts/Stores";
import CSVImport from "./Orders/CSV/CSVImport";
import Wallets from "./Wallets/Wallets";
import {
  consumeBuilderNext,
  getActiveDashboardAlerts,
  getBuilderRedirectURL,
  getRedirectURL,
  hasBuilderNext,
  isAllowedRedirect,
  isBlockedWalletAccount,
  isPublicHoliday,
} from "./common/helpers";
import MessageBlock from "./common/MessageBlock";
import { Spinner } from "react-bootstrap";
import { getWebToken } from "../actions/ActionHelpers";

const Home = (props) => {
  const [loading, setLoading] = useState(true);
  const { pathname, search } = useLocation();
  const [cookies, setCookie, removeCookie] = useCookies();
  const activeDashboardAlerts = getActiveDashboardAlerts();
  const showPublicHolidayBanner = isPublicHoliday();
  const isProduction = process.env?.REACT_APP_ENV
    ? process.env.REACT_APP_ENV === "production"
    : window.location.hostname.includes("shipshap.com");

  useEffect(() => {
    // * remove to make test mode work
    if (cookies.is_test) {
      removeCookie("is_test", {
        domain:
          document.location.hostname.split(".").slice(-2).join(".") ||
          document.location.hostname,
        path: "/",
      });
    }

    const organizationId = new URLSearchParams(search).get("org_id");
    if (organizationId) {
      props.setApplicationContext({
        organizationId,
      });
    }

    props
      .testSessionAuth()
      .then(async (res) => {
        if (hasBuilderNext()) {
          const token = await getWebToken();
          consumeBuilderNext();
          window.location.replace(getBuilderRedirectURL(token, props.auth));
          return;
        }

        props.getAccountInfo(
          async () => {
            await props.getBusinessDetails(
              () => {},
              () => {
                setLoading(false);
              },
            );
            await props.getOrganizations((orgs) => {
              const searchParams = new URLSearchParams(search);
              const hasOrgParam = searchParams.has("org_id");
              if (orgs.length <= 1 && props.context.organizationId) {
                props.setApplicationContext({
                  organizationId: undefined,
                });
              }
              if (orgs.length <= 1 && hasOrgParam) {
                searchParams.delete("org_id");
                window.location.replace(
                  `${pathname}?${searchParams.toString()}`,
                );
              }
              if (
                orgs.length > 1 &&
                !props.context.organizationId &&
                !hasOrgParam
              ) {
                props.setApplicationContext({
                  organizationId: orgs[0].id,
                });
                window.location.replace(
                  `${pathname}?${searchParams.toString()}`,
                );
              }
            });
          },
          () => setLoading(false),
        );

        props.getUserCountry();

        // * Checking when to redirect to dashboard, redirect to a return URL or stay
        // ? Maybe remove the redirect to dashboard to the Router, use <Redirect/> for ROUTES.HOME
        if (
          (!props.returnURL ||
            props.returnURL === "" ||
            props.returnURL === ROUTES.HOME) &&
          pathname === ROUTES.HOME
        ) {
          history.replace(ROUTES.DASHBOARD);
        } else if (
          props.returnURL &&
          props.returnURL !== "" &&
          props.returnURL !== ROUTES.HOME &&
          pathname !== ROUTES.LABEL + ROUTES.CREATE
        ) {
          history.replace(props.returnURL);
          props.setContextReturnURL(ROUTES.HOME);
        }
      })
      .catch((err) => {
        console.log(err);
        // * If there isn't a user logged in, save the return URL before going to the signin page
        props.setContextReturnURL(pathname + search);
        setLoading(false);
        history.replace(ROUTES.SIGN_IN);
      });
  }, []);

  useEffect(() => {
    if (hasBuilderNext()) return;
    if (Object.keys(props.info).length === 0) return;

    // * Since the user details and business details are requested concurrently -
    // * check the user details once we know the user details are loaded by checking if it is updating business details
    if (
      !props.businessDetails.loading &&
      (!props.info ||
        !props.info.full_name ||
        !props.info.phone_number ||
        !props.info.business_name ||
        !props.info.business_website ||
        !props.info.monthly_avg_shipment)
    ) {
      props.setApplicationContext({ returnURL: pathname }, () => {
        history.replace(ROUTES.UPDATE_ACCOUNT_DETAILS);
      });
      return;
    }
  }, [props.info, props.businessDetails.loading]);

  const hideWallet = isBlockedWalletAccount(props.auth, props.info?.email);

  if (loading) {
    return (
      <div className={"loading-screen"}>
        <div className="header d-flex flex-row align-items-center">
          <div>
            <BoxLogo />
          </div>
          <h2 className="mb-0">ShipShap</h2>
        </div>
      </div>
    );
  }

  const redirectToNewDashboard = async (route) => {
    const token = await getWebToken();
    const newDashboardWalletURL = getRedirectURL(
      route,
      token,
      props.auth,
      props.context?.organizationId,
    );
    if (isAllowedRedirect(route)) {
      window.location.replace(newDashboardWalletURL);
    }
  };

  return (
    <div className={`home-body d-flex align-items-center`}>
      <Toaster />
      <Switch>
        <Route
          path={[
            ROUTES.DASHBOARD,
            ROUTES.LABEL,
            ROUTES.ORDERS,
            ROUTES.CSV_IMPORT_PAGE,
            ROUTES.ACCOUNT,
            ROUTES.MANIFESTS,
            ROUTES.STORES,
            ROUTES.WALLETS,
            ROUTES.HELP_CENTER,
          ]}
          component={SideBar}
        />
      </Switch>
      <div className="home-content col-12 col-xl-10 ml-auto">
        <Header />
        {props.auth === SESSION_AUTH && (
          <div className={"impersonation-warning"}>
            <div
              className={
                "d-flex flex-row justify-content-start align-items-center"
              }
            >
              <FiAlertCircle />
              <p>
                {props.info?.is_impersonated
                  ? `You are impersonating the account of ${props.info?.full_name || props.info?.email || props.user?.email || "someone else"}`
                  : `You are signed in through admin dashboard account of ${props.info?.full_name || props.info?.email || props.user?.email}`}
              </p>
            </div>
            <button
              onClick={() => {
                props.logoutSession();
              }}
            >
              {" "}
              Log out{" "}
            </button>
          </div>
        )}
        {activeDashboardAlerts.map((alert, i) => (
          <MessageBlock
            key={`${alert.message}-${i}`}
            type={alert.type}
            icon={<FiAlertCircle />}
            message={alert.message}
          />
        ))}
        {showPublicHolidayBanner && (
          <div className={"impersonation-warning"}>
            <div
              className={
                "d-flex flex-row justify-content-start align-items-center"
              }
            >
              <FiAlertCircle />
              <p>ShipShap is closed for the public holiday.</p>
            </div>
          </div>
        )}
        {cookies.is_test && (
          <div className={`test-mode-banner d-flex`}>
            <div className="d-flex flex-row align-items-center justify-content-start">
              <TestGear className="mr-3" />
              <div>
                <h4 className="mb-0">You're in Testing Mode</h4>
                <p className="d-none d-lg-flex mb-0">
                  Every changes or transaction that you’ve made in this mode
                  will not recorded or process
                </p>
              </div>
            </div>
            <button
              className="d-none d-lg-flex"
              onClick={() => {
                removeCookie("is_test", {
                  domain:
                    document.location.hostname.split(".").slice(-2).join(".") ||
                    document.location.hostname,
                  path: "/",
                });
              }}
            >
              Back to Live Mode
            </button>
            <button
              className="d-flex d-lg-none"
              onClick={() => {
                removeCookie("is_test", {
                  domain:
                    document.location.hostname.split(".").slice(-2).join(".") ||
                    document.location.hostname,
                  path: "/",
                });
              }}
            >
              Live Mode
            </button>
          </div>
        )}
        {/*{	props.businessDetails&&*/}
        {/*	<div>*/}
        {/*		<Link to={ROUTES.BANK_ACCOUNT_DETAILS}>Verify Business</Link>*/}
        {/*	</div>*/}
        {/*}*/}
        <Switch>
          <Route
            path={ROUTES.DASHBOARD}
            exact
            component={() => {
              redirectToNewDashboard(ROUTES.DASHBOARD);
              return isAllowedRedirect(ROUTES.DASHBOARD) ? (
                <Spinner />
              ) : (
                <Dashboard />
              );
            }}
          />
          <Route
            path={ROUTES.LABEL}
            exact
            component={() => {
              redirectToNewDashboard(ROUTES.LABEL);
              return isAllowedRedirect(ROUTES.LABEL) ? <Spinner /> : <Label />;
            }}
          />
          {/* <Route path={ ROUTES.TRACKING} exact component={Track} /> */}
          {/*<Route path={ ROUTES.GIFTS} component={Gifts} />*/}
          <Route path={ROUTES.ACCOUNT} component={Account} />
          <Route
            path={ROUTES.ORDERS}
            exact
            component={() => {
              redirectToNewDashboard(ROUTES.ORDERS);
              return isAllowedRedirect(ROUTES.ORDERS) ? (
                <Spinner />
              ) : (
                <Orders />
              );
            }}
          />
          <Route
            path={ROUTES.MANIFESTS}
            exact={!isAllowedRedirect(ROUTES.MANIFESTS)}
            component={() => {
              redirectToNewDashboard(ROUTES.MANIFESTS);
              return isAllowedRedirect(ROUTES.MANIFESTS) ? (
                <Spinner />
              ) : (
                <Manifests />
              );
            }}
          />
          <Route
            path={ROUTES.MANIFESTS + ROUTES.CREATE}
            exact
            component={CreateManifest}
          />
          <Route
            path={ROUTES.MANIFESTS + "/:id" + ROUTES.ADD_LABELS}
            component={AddMoreLabels}
          />
          <Route path={ROUTES.MANIFESTS + "/:id"} component={ManifestDetails} />
          <Route path={ROUTES.LABEL + ROUTES.CREATE} component={CreateLabel} />
          <Route
            path={ROUTES.STORES}
            component={() => {
              redirectToNewDashboard(ROUTES.STORES);
              return isAllowedRedirect(ROUTES.STORES) ? (
                <Spinner />
              ) : (
                <Stores />
              );
            }}
          />
          <Route
            path={ROUTES.HELP_CENTER}
            component={() => {
              redirectToNewDashboard(ROUTES.HELP_CENTER);
              return isAllowedRedirect(ROUTES.HELP_CENTER) ? (
                <Spinner />
              ) : (
                <HelpCenter />
              );
            }}
          />
          <Route path={ROUTES.CSV_IMPORT_PAGE} component={CSVImport} />
          {!hideWallet && (
            <Route
              path={ROUTES.WALLETS}
              component={() => {
                redirectToNewDashboard(ROUTES.WALLETS);
                return isAllowedRedirect(ROUTES.WALLETS) ? (
                  <Spinner />
                ) : (
                  <Wallets />
                );
              }}
            />
          )}
          <Route
            render={() => {
              return <Redirect to={ROUTES.DASHBOARD} />;
            }}
          />
        </Switch>
      </div>
    </div>
  );
};

const mapStateToProps = ({
  auth,
  userDetails,
  businessDetails,
  applicationContext,
}) => ({
  user: auth.user,
  info: userDetails.info,
  userDetails,
  businessDetails,
  returnURL: applicationContext.context.returnURL,
  auth: applicationContext.context.auth,
  context: applicationContext.context,
});

export default connect(mapStateToProps, {
  getAccountInfo,
  getBusinessDetails,
  setContextReturnURL,
  setApplicationContext,
  getUserCountry,
  testSessionAuth,
  logoutSession,
  getOrganizations,
})(Home);
